import { onBeforeRouteLeave, useRoute } from 'vue-router'
import { PATH } from '@/constants'
import { onBeforeMount, onUnmounted, ref } from 'vue'
import { getCookie } from '@/utils'
import commonService from '@/services/common.service'
import dayjs from 'dayjs'
import { CHAT_STORE } from '@/store/constants'
import { useStore } from 'vuex'

export const getToken = async () => {
  const CSRFToken = await commonService.getCSRFToken()
  return CSRFToken?.data?.data.token
}

interface UseWebSocketOptions {
  onConnected?: () => void
}

const MAX_BACKOFF_DELAY = 15000

export function useWebSocket(type: 'chatChannel' | 'talkChannel' = 'chatChannel', options?: UseWebSocketOptions) {
  const connection = ref<WebSocket | null>(null)

  const route = useRoute()
  const store = useStore()

  const token = ref()
  let isManualClose = false
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let retryCount = 0

  onBeforeMount(async () => {
    window.addEventListener('beforeunload', leaving)
    openSocket()
  })

  const setCSRFToken = async () => {
    const CSRFToken = await commonService.getCSRFToken()
    token.value = CSRFToken?.data?.data.token
  }

  const cleanupSocket = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }

    if (connection.value) {
      // Remove all event handlers from the old socket to prevent accidental onclose/onerror calls.
      connection.value.onopen = null
      connection.value.onmessage = null
      connection.value.onerror = null
      connection.value.onclose = null

      if (connection.value.readyState === WebSocket.OPEN || connection.value.readyState === WebSocket.CONNECTING) {
        connection.value.close()
      }
      connection.value = null
    }
  }

  const openSocket = async () => {
    const path = PATH[`${route.path.split('/')[1]}`]
    if (!path?.header) return

    // Clean up the old connection before opening a new one
    cleanupSocket()

    await setCSRFToken()
    let headers = { 'access-token': token.value }
    let url = `${process.env.VUE_APP_WS_ENDPOINT}`

    Object.keys(headers).forEach((e, i) => {
      url += `${i == 0 ? '?' : '&'}${e}=${encodeURIComponent(headers[e])}`
    })

    const ws = new WebSocket(url)
    connection.value = ws

    const objIdentifier = {
      chatChannel: '{"channel":"ChatChannel"}',
      talkChannel: '{"channel":"TalkChannel"}',
    }

    ws.onopen = () => {
      ws?.send(
        JSON.stringify({
          command: 'subscribe',
          identifier: objIdentifier[type],
        })
      )

      retryCount = 0

      if (options?.onConnected) {
        options.onConnected()
      }
    }

    ws.onmessage = (mess) => {
      const message = JSON.parse(mess.data)?.message
      store.dispatch(CHAT_STORE.ACTIONS.SET_MESSAGE_SOCKET, message)
    }

    ws.onerror = (e) => {
      console.log(e)
    }

    ws.onclose = () => {
      if (!isManualClose) {
        const delay = Math.min(1000 * Math.pow(2, retryCount), 15000) // 1s, 2s ..., 15s

        // Keep retryCount from exceeding 4 (since the delay is consistently 15s from the 4th attempt onwards)
        if (delay < MAX_BACKOFF_DELAY) {
          retryCount++
        }
        reconnectTimer = setTimeout(() => openSocket(), delay)
      }
    }
  }

  const leaving = () => {
    isManualClose = true
    cleanupSocket()
  }

  onUnmounted(() => {
    window.removeEventListener('beforeunload', leaving)
    leaving()
  })

  const forceReconnect = async () => {
    // Only reconnect if the socket is disconnected or the disconnection is incomplete.
    if (!connection.value || connection.value.readyState !== WebSocket.OPEN) {
      isManualClose = false
      await openSocket()
    }
  }

  return { connection, reconnect: forceReconnect }
}
